/*
* === HomePage.js (Final - Hub & Spoke Cross-Domain Routing with Failsafe) ===
*/
import React, { useEffect, useState, useMemo } from 'react';
import { tenantConfig } from '../../config/tenantConfig';
import { 
    Container, Typography, Box, Grid, Button, Card, CardContent, 
    Chip, TextField, InputAdornment, MenuItem, Select, FormControl, 
    InputLabel, Skeleton, Paper, Avatar, useTheme
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { isProfileIncomplete } from '../../services/profileGuard';

import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import SpeedIcon from '@mui/icons-material/Speed';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ArticleIcon from '@mui/icons-material/Article';
import OpenInNewIcon from '@mui/icons-material/OpenInNew'; 

const HeroSection = ({ onExploreClick }) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const funderRegisterUrl = `https://brightsparc.co.za/auth?mode=register&type=Funder&source=${tenantConfig.id}`;

    return (
        <Box sx={{
            bgcolor: isDark ? 'background.paper' : 'primary.main', 
            color: isDark ? 'text.primary' : 'white', 
            pt: 12, pb: 10,
            clipPath: 'polygon(0 0, 100% 0, 100% 90%, 0 100%)',
            borderBottom: isDark ? `1px solid ${theme.palette.divider}` : 'none'
        }}>
            <Container maxWidth="lg">
                <Grid container spacing={4} alignItems="center">
                    <Grid item xs={12} md={7}>
                        <Typography variant="h2" fontWeight="800" gutterBottom>{tenantConfig.heroTitle}</Typography>
                        <Typography variant="h6" sx={{ mb: 4, opacity: 0.9, lineHeight: 1.6, color: isDark ? 'text.secondary' : 'inherit' }}>
                            {tenantConfig.heroSubtitle}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Button variant="contained" color={isDark ? "primary" : "secondary"} size="large" onClick={onExploreClick} sx={{ px: 4, py: 1.5, fontWeight: 'bold' }}>
                                Find Your Bursary
                            </Button>
                            <Button 
                                variant="outlined" 
                                color={isDark ? "primary" : "inherit"} 
                                size="large"
                                {...(tenantConfig.id === 'BRIGHTSPARC' 
                                    ? { onClick: () => navigate('/auth?mode=register&type=Funder') } 
                                    : { component: 'a', href: funderRegisterUrl, target: '_blank', rel: 'noopener noreferrer' }
                                )}
                                sx={{ px: 4, py: 1.5 }}
                            >
                                Become a Funder
                            </Button>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={5} sx={{ display: { xs: 'none', md: 'block' }, textAlign: 'center' }}>
                         <Box component="img" src="/assets/hero-illustration.svg" alt="BrightSparc Application Flow" sx={{ width: '100%', maxWidth: 400, opacity: isDark ? 0.7 : 0.9 }} onError={(e) => e.target.style.display = 'none'} />
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

const FeaturesSection = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const features = [
        { title: "Real-Time Eligibility Engine", description: "Stop wasting time. Our smart engine matches your profile to funds you actually qualify for, instantly.", icon: <SpeedIcon fontSize="large" color={isDark ? "inherit" : "primary"} /> },
        { title: "Automated Verifications", description: "Built-in real-time South African ID and Bank Account verification for absolute trust and compliance.", icon: <VerifiedUserIcon fontSize="large" color={isDark ? "inherit" : "primary"} /> },
        { title: "Digital Document Vault", description: "Enterprise-grade, secure cloud storage for your IDs, transcripts, and affidavits with auto-expiry tracking.", icon: <SecurityIcon fontSize="large" color={isDark ? "inherit" : "primary"} /> },
        { title: "Auto-PDF Generation", description: "Applications are automatically compiled into beautifully formatted, downloadable PDF documents for offline review.", icon: <ArticleIcon fontSize="large" color={isDark ? "inherit" : "primary"} /> }
    ];

    return (
        <Box sx={{ py: 8, bgcolor: 'background.default' }}>
            <Container maxWidth="lg">
                <Typography variant="h4" align="center" fontWeight="bold" gutterBottom color="text.primary">
                    Why Choose {tenantConfig.name}?
                </Typography>
                <Typography variant="subtitle1" align="center" color="text.secondary" sx={{ mb: 6 }}>
                    {tenantConfig.heroDescription}
                </Typography>
                <Grid container spacing={4}>
                    {features.map((feature, idx) => (
                        <Grid item xs={12} sm={6} md={3} key={idx}>
                            <Card elevation={0} sx={{ height: '100%', textAlign: 'center', bgcolor: 'transparent' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                    <Avatar sx={{ bgcolor: isDark ? 'action.selected' : 'primary.light', color: isDark ? 'primary.light' : 'primary.main', width: 64, height: 64 }}>
                                        {feature.icon}
                                    </Avatar>
                                </Box>
                                <Typography variant="h6" fontWeight="bold" gutterBottom color="text.primary">{feature.title}</Typography>
                                <Typography variant="body2" color="text.secondary">{feature.description}</Typography>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box>
    );
};

const FunderCTABanner = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const funderRegisterUrl = `https://brightsparc.co.za/auth?mode=register&type=Funder&source=${tenantConfig.id}`;
    const bannerBackground = isDark ? 'linear-gradient(135deg, #1A2027 0%, #2D3748 100%)' : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)';

    return (
        <Container sx={{ mt: 2, mb: 6 }}>
            <Paper elevation={isDark ? 2 : 4} sx={{ p: { xs: 3, md: 5 }, borderRadius: 3, background: bannerBackground }}>
                <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={8}>
                        <Typography variant="h5" fontWeight="bold" gutterBottom color="text.primary">Corporate CSI & Independent Sponsors</Typography>
                        <Typography variant="body1" sx={{ color: isDark ? 'text.secondary' : 'rgba(0,0,0,0.7)' }}>
                            Leverage custom scoring rubrics, automated workflows, and flexible subscription tiers to manage your bursary cohort effortlessly.
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                        <Button 
                            variant="contained" color="primary" size="large" startIcon={<PersonAddIcon />}
                            {...(tenantConfig.id === 'BRIGHTSPARC' ? { onClick: () => navigate('/auth?mode=register&type=Funder') } : { component: 'a', href: funderRegisterUrl, target: '_blank', rel: 'noopener noreferrer' })}
                            sx={{ py: 1.5, px: 3 }}
                        >
                            Create Funder Account
                        </Button>
                    </Grid>
                </Grid>
            </Paper>
        </Container>
    );
};

const ReviewsSection = () => {
    const [reviews, setReviews] = useState([]);
    const theme = useTheme();

    useEffect(() => {
        const fetchReviews = async () => {
            try {
                const { data } = await api.get('/public/reviews');
                setReviews(Array.isArray(data) ? data : []);
            } catch (error) { setReviews([]); }
        };
        fetchReviews();
    }, []);

    if (!reviews || reviews.length === 0) return null;

    return (
        <Box sx={{ py: 8, bgcolor: 'background.paper', borderTop: `1px solid ${theme.palette.divider}` }}>
            <Container maxWidth="lg">
                <Typography variant="h4" align="center" fontWeight="bold" gutterBottom color="text.primary">What Our Users Say</Typography>
                <Grid container spacing={4} sx={{ mt: 2 }}>
                    {reviews?.map((review) => (
                        <Grid item xs={12} md={4} key={review.Id}>
                            <Card elevation={2} sx={{ height: '100%', borderRadius: 2, bgcolor: 'background.default' }}>
                                <CardContent>
                                    <FormatQuoteIcon color="action" fontSize="large" sx={{ transform: 'rotate(180deg)' }} />
                                    <Typography variant="body1" sx={{ mb: 2, fontStyle: 'italic', color: 'text.secondary' }}>"{review.ReviewText}"</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto' }}>
                                        <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>{review.FirstNames ? review.FirstNames[0] : 'U'}</Avatar>
                                        <Box>
                                            <Typography variant="subtitle2" fontWeight="bold" color="text.primary">{review.FirstNames} {review.Surname}</Typography>
                                            <Typography variant="caption" color="text.secondary">{review.Role}</Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box>
    );
};

// --- Bursary Card ---
const PublicBursaryCard = ({ bursary, onApply, applicationStatus, navigate, userRole }) => {
    const isClosed = new Date(bursary.ApplicationEndDate) < new Date();
    
    // 🚨 BULLETPROOF FAILSAFE: Check both Sponsor and FundName for tenant routing
    const isJBMarks = (bursary.Sponsor || '').toLowerCase().includes('jb marks') || 
                      (bursary.FundName || '').toLowerCase().includes('jb marks') ||
                      (bursary.FundName || '').toLowerCase().includes('jbm');
                      
    const resolvedDomain = bursary.TenantDomain || (isJBMarks ? 'https://applications.jbmarksedutrust.co.za' : null);
    const isExternalTenant = tenantConfig.id === 'BRIGHTSPARC' && !!resolvedDomain;
    
    let btnText = isExternalTenant ? "Apply on Tenant Portal" : "Apply Now";
    let btnColor = "primary";
    let btnVariant = "contained";
    let isDisabled = false;

    if (userRole && userRole !== 'Applicant') {
        btnText = "Applicants Only"; isDisabled = true; btnVariant = "outlined"; btnColor = "secondary";
    } else if (isClosed) {
        btnText = "Closed"; isDisabled = true; btnVariant = "outlined"; btnColor = "error";
    } else if (applicationStatus) {
        if (applicationStatus === 'Draft') { btnText = "Continue Draft"; btnColor = "warning"; } 
        else if (applicationStatus === 'Withdrawn') { btnText = "Apply Again (Withdrawn)"; btnColor = "primary"; } 
        else { btnText = applicationStatus; isDisabled = true; btnColor = "success"; }
    }

    const handleViewDetails = () => {
        if (isExternalTenant) {
            const baseUrl = resolvedDomain.replace(/\/$/, '');
            window.location.href = `${baseUrl}/bursary/${bursary.Id}`;
        } else {
            navigate(`/bursary/${bursary.Id}`);
        }
    };

    return (
        <Card elevation={3} sx={{ 
            height: '100%', display: 'flex', flexDirection: 'column', transition: '0.3s', 
            bgcolor: 'background.paper', '&:hover': { transform: 'translateY(-5px)', boxShadow: 6 } 
        }}>
            <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Chip label={bursary.FundCategory} size="small" color="secondary" />
                    {applicationStatus && !['Draft', 'Withdrawn'].includes(applicationStatus) && <CheckCircleIcon color="success" />}
                </Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom color="text.primary">{bursary.FundName}</Typography>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>By {bursary.Sponsor}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {bursary.Description ? bursary.Description.replace(/<[^>]+>/g, '') : 'No description provided.'}
                </Typography>
                
                <Button size="small" sx={{ textTransform: 'none', p: 0, fontWeight: 'bold' }} onClick={handleViewDetails}>
                    View Full Requirements &rarr;
                </Button>
            </CardContent>

            <Box sx={{ p: 2, pt: 0 }}>
                <Button 
                    variant={btnVariant} color={btnColor} fullWidth onClick={() => onApply(bursary, applicationStatus)}
                    disabled={isDisabled} sx={{ py: 1 }} endIcon={isExternalTenant && !isDisabled ? <OpenInNewIcon /> : null}
                >
                    {btnText}
                </Button>
            </Box>
        </Card>
    );
};

// --- Main HomePage Component ---
const HomePage = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams(); 
    const { user, token } = useAuth();
    const theme = useTheme();
    
    const [bursaries, setBursaries] = useState([]);
    const [userApps, setUserApps] = useState({});
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [fieldFilter, setFieldFilter] = useState(searchParams.get('field') || '');

    // 🚨 FIXED: Define userProfile state locally inside the HomePage scope so it is defined!
    const [userProfile, setUserProfile] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [fundsRes, appsRes, profileRes] = await Promise.all([
                    api.get('/public/bursaries'),
                    (token && (user?.role === 'Applicant' || user?.Role === 'Applicant')) 
                        ? api.get('/applicant/dashboard', { headers: { Authorization: `Bearer ${token}` } })
                        : Promise.resolve({ data: [] }),
                    // 🚨 FIXED: Fetch the fresh profile properties directly in the parallel thread layout
                    (token && (user?.role === 'Applicant' || user?.Role === 'Applicant'))
                        ? api.get('/applicant/profile', { headers: { Authorization: `Bearer ${token}` } })
                        : Promise.resolve({ data: null })
                ]);

                const allFunds = Array.isArray(fundsRes.data) ? fundsRes.data : [];
                const activeFunds = allFunds.filter(b => b.IsActive !== 0 && b.IsActive !== false && b.IsActive !== '0');
                const uniqueFunds = Array.from(new Map(activeFunds.map(item => [item.Id, item])).values());
                
                setBursaries(uniqueFunds);
                
                // 🚨 FIXED: Store the fresh profile data frames
                if (profileRes && profileRes.data) {
                    setUserProfile(profileRes.data);
                }
                
                const appsMap = {};
                if (appsRes.data && Array.isArray(appsRes.data)) {
                    appsRes.data.forEach(app => appsMap[app.FundId] = app.Status);
                }
                setUserApps(appsMap);
                
                if (searchParams.get('field')) {
                    setTimeout(() => { document.getElementById('bursaries-grid')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 500);
                }
            } catch (err) { console.error("Failed to load home data", err); }
            finally { setLoading(false); }
        };
        fetchData();
    }, [token, user, searchParams]);

    const handleApply = async (bursary, currentStatus) => {       
        // 🚨 THE FIX: Explicitly allow 'Draft' and 'Withdrawn' to pass through!
        if (currentStatus && !['Draft', 'Withdrawn'].includes(currentStatus)) return;

        const isJBMarks = bursary.Sponsor?.toLowerCase().includes('jb marks');
        const resolvedDomain = bursary.TenantDomain || (isJBMarks ? 'https://applications.jbmarksedutrust.co.za' : null);
        const isExternalTenant = tenantConfig.id === 'BRIGHTSPARC' && !!resolvedDomain;

        if (isExternalTenant) {
            const baseUrl = resolvedDomain.replace(/\/$/, '');
            window.location.href = `${baseUrl}/auth?redirect=/applicant/form?fundId=${bursary.Id}`;
            return;
        }

        if (user) { 
            try {
                const res = await api.get('/applicant/profile', { 
                    headers: { Authorization: `Bearer ${token}` } 
                });
                
                const profileData = res.data;

                if (!profileData || isProfileIncomplete(profileData)) {
                    navigate('/profile?reason=incomplete_profile');
                    return;
                }

            } catch (err) {
                console.error("Interceptor safety fallback:", err);
            }

            navigate(`/applicant/form?fundId=${bursary.Id}`);
        } else { 
            navigate(`/auth?redirect=/applicant/form?fundId=${bursary.Id}`);
        }
    };

    const handleFieldFilterChange = (e) => {
        const val = e.target.value;
        setFieldFilter(val);
        if (val) setSearchParams({ field: val });
        else setSearchParams({});
    };

    const filteredBursaries = useMemo(() => {
        return bursaries.filter(b => {
            const matchesSearch = b.FundName.toLowerCase().includes(searchTerm.toLowerCase()) || b.Sponsor.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesField = fieldFilter ? b.FundCategory === fieldFilter : true;
            return matchesSearch && matchesField;
        });
    }, [bursaries, searchTerm, fieldFilter]);

    return (
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
            <HeroSection onExploreClick={() => document.getElementById('bursaries-grid').scrollIntoView({ behavior: 'smooth', block: 'start' })} />
            <FeaturesSection />
            <FunderCTABanner />

            <Container id="bursaries-grid" maxWidth="lg" sx={{ py: 4, mb: 8 }}>
                <Typography variant="h4" fontWeight="bold" gutterBottom color="text.primary">Open Bursaries & Scholarships</Typography>
                <Box sx={{ 
                    mb: 4, mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap', 
                    bgcolor: 'background.paper', p: 2, borderRadius: 2, 
                    boxShadow: theme.palette.mode === 'dark' ? 2 : 1,
                    border: theme.palette.mode === 'dark' ? `1px solid ${theme.palette.divider}` : 'none'
                }}>
                    <TextField 
                        placeholder="Search by fund name or sponsor..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                        variant="outlined" InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} sx={{ flexGrow: 1 }}
                    />
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>Filter by Field</InputLabel>
                        <Select value={fieldFilter} label="Filter by Field" onChange={handleFieldFilterChange}>
                            <MenuItem value="">All Fields</MenuItem>
                            <MenuItem value="Engineering">Engineering</MenuItem>
                            <MenuItem value="Science">Science</MenuItem>
                            <MenuItem value="Technology">Technology</MenuItem>
                            <MenuItem value="Health Sciences">Health Sciences</MenuItem>
                            <MenuItem value="Commerce">Commerce</MenuItem>
                            <MenuItem value="Arts">Arts</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                <Grid container spacing={4}>
                    {loading ? (
                        [1, 2, 3].map(i => (
                            <Grid item xs={12} sm={6} md={4} key={i}>
                                <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2, bgcolor: 'action.hover' }} />
                            </Grid>
                        ))
                    ) : filteredBursaries.length > 0 ? (
                        filteredBursaries.map(bursary => (
                            <Grid item xs={12} sm={6} md={4} key={bursary.Id}>
                                <PublicBursaryCard bursary={bursary} onApply={handleApply} applicationStatus={userApps[bursary.Id]} navigate={navigate} userRole={user?.role || user?.Role} />
                            </Grid>
                        ))
                    ) : (
                        <Grid item xs={12}>
                            <Box sx={{ textAlign: 'center', py: 8 }}>
                                <Typography variant="h6" color="text.secondary">No bursaries found matching your criteria.</Typography>
                                <Button variant="outlined" onClick={() => {setSearchTerm(''); handleFieldFilterChange({target: {value: ''}});}} sx={{ mt: 2 }}>Clear Filters</Button>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </Container>
            <ReviewsSection />
        </Box>
    );
};

export default HomePage;